import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PhotoGrid from '../components/shared/PhotoGrid';
import FullScreenView from '../components/shared/FullScreenView';
import ApproveRejectButtons from '../components/artist/ApproveRejectButtons';
import ExpiredLink from '../components/artist/ExpiredLink';
import mbLogo from '../assets/milwaukee-ballet-logo.png';
import agmaMark from '../assets/agma-mark.png';
import { apiFetch } from '../utils/api';

const URGENT_THRESHOLD_MS = 2 * 60 * 60 * 1000;

function formatRemaining(ms) {
  if (ms <= 0) return null;
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} minute${minutes === 1 ? '' : 's'} remaining`;
  if (minutes === 0) return `${hours} hour${hours === 1 ? '' : 's'} remaining`;
  return `${hours} hour${hours === 1 ? '' : 's'} ${minutes} minute${minutes === 1 ? '' : 's'} remaining`;
}

export default function ArtistGallery() {
  const { token } = useParams();

  const [fetchState, setFetchState] = useState({
    token: null,
    error: null,
    errorStatus: null,
    photos: [],
    expiresAt: null,
    windowClosed: false,
  });
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    if (!token) return;

    apiFetch(`/api/shared-links/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(body.message || `Request failed (${res.status})`);
          err.status = res.status;
          throw err;
        }
        return res.json();
      })
      .then(({ photos: fetchedPhotos, expiresAt }) => {
        const windowClosed = expiresAt
          ? Date.now() > new Date(expiresAt).getTime()
          : false;
        setFetchState({
          token,
          error: null,
          errorStatus: null,
          photos: fetchedPhotos ?? [],
          expiresAt,
          windowClosed,
        });
      })
      .catch((err) => {
        setFetchState({
          token,
          error: err.message,
          errorStatus: err.status ?? null,
          photos: [],
          expiresAt: null,
          windowClosed: false,
        });
      });
  }, [token]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!fetchState.expiresAt || fetchState.windowClosed) return;
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, [fetchState.expiresAt, fetchState.windowClosed]);

  const handleDecision = useCallback((photoId, newApprovalStatus) => {
    setFetchState((prev) => ({
      ...prev,
      photos: prev.photos.map((p) =>
        p.id === photoId
          ? { ...p, credit: { ...p.credit, approvalStatus: newApprovalStatus } }
          : p
      ),
    }));
  }, []);

  const loading = token != null && fetchState.token !== token;
  const { error, photos, expiresAt } = fetchState;

  const creditMap = new Map(photos.map((p) => [p.id, p.credit?.approvalStatus]));

  const approved = photos.filter((p) =>
    ['approved', 'auto_approved'].includes(p.credit?.approvalStatus)).length;
  const rejected = photos.filter((p) => p.credit?.approvalStatus === 'rejected').length;
  const pending = photos.filter(
    (p) => !p.credit?.approvalStatus || p.credit.approvalStatus === 'pending'
  ).length;
  const allDecided = photos.length > 0 && pending === 0;

  const activePhoto = activeIndex !== null ? photos[activeIndex] : null;

  const allAutoApproved = (
    photos.length > 0 &&
    photos.every((p) => p.credit && p.credit.approvalStatus === 'auto_approved')
  );
  const windowClosed = fetchState.windowClosed;
  const isPostWindow = allAutoApproved && windowClosed;

  const expiryLabel = expiresAt
    ? new Date(expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  const remainingMs = expiresAt ? new Date(expiresAt).getTime() - now : null;
  const remainingLabel = remainingMs !== null ? formatRemaining(remainingMs) : null;
  const isUrgent = remainingMs !== null && remainingMs > 0 && remainingMs <= URGENT_THRESHOLD_MS;

  const isClosedWindow = fetchState.errorStatus === 401
    || fetchState.errorStatus === 403
    || fetchState.errorStatus === 410;

  // Build the page content based on state
  let content;
  if (loading) {
    content = (
      <div style={{ padding: 32, textAlign: 'center', color: '#555' }}>
        Loading your photos…
      </div>
    );
  } else if (error) {
    content = isClosedWindow ? <ExpiredLink /> : (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <p style={{ color: '#dc2626', fontSize: 18, marginBottom: 8 }}>⚠ {error}</p>
        <p style={{ color: '#555', fontSize: 14 }}>
          If you believe this is a mistake, contact your administrator.
        </p>
      </div>
    );
  } else if (photos.length === 0) {
    content = (
      <div style={{ padding: 32, textAlign: 'center', color: '#555' }}>
        No photos are waiting for your review right now.
      </div>
    );
  } else {
    content = (
      <>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: 'var(--color-text)' }}>Your Photos</h2>

        {expiryLabel && !windowClosed && !isPostWindow && (
          <div style={{
            padding: '12px 20px', margin: '0 0 16px 0',
            backgroundColor: isUrgent ? '#FDECEC' : '#EAF2FB',
            border: `1px solid ${isUrgent ? '#DC2626' : '#3B76D9'}`,
            borderRadius: 6,
          }} role='status'>
            <strong style={{ display: 'block', color: isUrgent ? '#7B1C1C' : '#1E3A6E' }}>
              Action required by {expiryLabel}
            </strong>
            {remainingLabel && (
              <span style={{ color: isUrgent ? '#7B1C1C' : '#1E3A6E', fontSize: 13 }}>
                {remainingLabel}
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
          <span style={{ color: '#1A5C25' }}>✓ {approved} approved</span>
          <span style={{ color: '#7B1C1C' }}>✕ {rejected} rejected</span>
          {pending > 0 && <span style={{ color: '#888' }}>{pending} pending</span>}
        </div>

        {isPostWindow && (
          <div style={{
            padding: '16px 20px', margin: '12px 0 20px 0',
            backgroundColor: '#FFF6E0', border: '1px solid #D9A441', borderRadius: 6,
          }} role='status'>
            <strong style={{ display: 'block', marginBottom: 4 }}>
              This approval window has closed.
            </strong>
            <span style={{ color: '#7a4f00' }}>
              All photos in this batch were auto-approved per the collective bargaining
              agreement when the 24-hour window expired.
              You can still view the photos below.
              If anything looks wrong, please contact the Milwaukee Ballet Marketing Admin.
            </span>
          </div>
        )}

        {!isPostWindow && allDecided && (
          <div style={{
            padding: '16px 20px', margin: '12px 0 20px 0',
            backgroundColor: '#EAF6E6', border: '1px solid #2E8B57', borderRadius: 6,
          }} role='status'>
            <strong style={{ display: 'block', marginBottom: 4 }}>
              You've reviewed all photos in this batch.
            </strong>
            <span style={{ color: '#1d4f33' }}>
              Your responses have been recorded. {approved} approved, {rejected} rejected.
              Revisions can be made to any rejected photos while the review window remains open. 
            </span>
          </div>
        )}

        <PhotoGrid
          photos={photos}
          onSelect={setActiveIndex}
          creditMap={creditMap}
          columns={3}
        />

        <FullScreenView
          photo={activePhoto}
          onClose={() => setActiveIndex(null)}
          onPrev={() => setActiveIndex((i) => Math.max(0, i - 1))}
          onNext={() => setActiveIndex((i) => Math.min(photos.length - 1, i + 1))}
          hasPrev={activeIndex > 0}
          hasNext={activeIndex !== null && activeIndex < photos.length - 1}
          actionArea={
            activePhoto && (
              <ApproveRejectButtons
                key={activePhoto.id}
                photo={activePhoto}
                token={token}
                onDecision={handleDecision}
              />
            )
          }
        />
      </>
    );
  }

  return (
    <div className='gallery-shell'>
      <header className='app-header'>
        <div className='app-header-brand'>
          <img src={mbLogo} alt='Milwaukee Ballet' className='app-logo' />
          <h1 className='app-title'>PHOTO APPROVAL</h1>
          <h3 className='app-role'>Artist Portal</h3>
        </div>
      </header>

      <main className='gallery-main'>
        {content}

        <div className='gallery-portfolio-cta'>
          <Link to='/login' className='gallery-portfolio-link'>
            Sign in to view your portfolio →
          </Link>
        </div>
      </main>

      <footer className='app-footer'>
        <img src={agmaMark} alt='AGMA' className='app-footer-mark' />
        <span className='app-footer-text'>
          This system implements the photo approval rights guaranteed under the
          AGMA Collective Bargaining Agreement, Section 4, Article 41.
        </span>
      </footer>
    </div>
  );
}